import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, register } from '../api.js';
import { LOGIN_PATH, PASSWORD_MIN_LENGTH, passwordProblem } from '../lib/auth.js';
import GirisKabugu, {
  AnimateIn,
  AlanEtiketi,
  HataSeridi,
  KartAciklama,
  KartBaslik,
  MarkaButonu,
  MetinAlani,
  ParolaAlani,
  UST_HAP,
} from '../components/GirisKabugu.js';

/**
 * §43.2 — `/kayit`: self-serve kayıt sayfası.
 *
 * GÖRSEL DİL `GirisKabugu` ile referans admin paneline taşındı; DAVRANIŞ, tüm
 * `data-testid`ler ve kullanıcı metinleri önceki sürümle birebir aynıdır.
 *
 * Sayfa route guard'ın DIŞINDADIR (§27.5 muafiyet mantığının §43 karşılığı): kayıt
 * olan kişinin HENÜZ oturumu yoktur; guard'ın arkasında olsaydı linke tıklayan herkes
 * giriş sayfasına düşerdi.
 *
 * KAYIT OTURUM AÇMAZ (davet kabulünden farkı budur, §27.4 `accept` otomatik login
 * eder): §43.2 gereği doğrulanmamış kullanıcı giriş bile YAPAMAZ, dolayısıyla burada
 * verilecek tek doğru yön "gelen kutuna bak"tır.
 */

/**
 * §43.1 — "kayıt kapalıysa panel kayıt sayfasını hiç çizmez".
 *
 * Bayrağı SORAN bir uç yoktur ve panel MOUNT'TA yoklama İSTEĞİ DE ATMAZ: bayrak
 * açıkken `POST /auth/register` §43.4 IP tavanını (günde 20) tüketir; sırf "açık mı"
 * diye sormak kullanıcının kendi kotasını yerdi. Bu yüzden kapalılık GÖNDERİMDE
 * öğrenilir — sunucu 404 döner ve form yerine bu kart çıkar.
 */
function isSignupClosed(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * §43.2 hata sözleşmesi DURUM KODU + `ApiError.code` ikilisinden okunur; 400 altında
 * İKİ ayrı kural yaşar (`invalid_email` / `weak_password`) ve kullanıcının yapacağı
 * düzeltme ikisinde farklıdır.
 */
function registerErrorText(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.status === 400) {
      if (error.code === 'weak_password') {
        return `Parola politikaya uymuyor: en az ${PASSWORD_MIN_LENGTH} karakter, 1 harf ve 1 rakam.`;
      }
      if (error.code === 'invalid_email') return 'Geçerli bir e-posta adresi girin.';
      return error.message;
    }
    // §43.4 — tavan GÜNLÜKTÜR (login'in 15 dakikalık penceresiyle karıştırılmamalı).
    if (error.status === 429) {
      return 'Bu ağdan çok fazla kayıt denemesi yapıldı — yarın tekrar deneyin.';
    }
    if (error.status === 0) return 'Sunucuya ulaşılamadı — bağlantınızı kontrol edin.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * Üst çubuk sağı — referansın "Don't have an account? Sign up" hapının tersi.
 *
 * `kayit-giris-linki` testid'i BURADADIR ve kabuk her durumda çizdiği için üç kart
 * hâlinde de (form / e-postanı kontrol et / kapalı) mevcuttur — eski sürüm aynı
 * testid'i durum başına ayrı ayrı kopyalıyordu.
 */
function GirisHapi() {
  return (
    <span className="flex items-center gap-3">
      <span className="font-grotesk text-[13px] text-[#686868]">Hesabınız var mı?</span>
      <Link data-testid="kayit-giris-linki" to={LOGIN_PATH} className={UST_HAP}>
        Giriş yapın
      </Link>
    </span>
  );
}

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  // Sunucuya gitmeden önce politikayı söylemek için; asıl karar §27.3 ile sunucudadır.
  const [localProblem, setLocalProblem] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      register({
        email: email.trim().toLowerCase(),
        password,
        fullName: fullName.trim(),
      }),
  });

  if (isSignupClosed(submit.error)) {
    return (
      <GirisKabugu testId="kayit-sayfasi" duzen="ortala" ustSag={<GirisHapi />}>
        <div data-testid="kayit-kapali" className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>Kayıt kapalı</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60} className="flex flex-col gap-4">
            <KartAciklama>
              Bu kurulumda self-serve kayıt açık değil. Hesaplar yalnız davetle açılır —
              proje sahibinden davet isteyin.
            </KartAciklama>
            <Link to={LOGIN_PATH} className="w-full">
              <MarkaButonu type="button">Girişe dön</MarkaButonu>
            </Link>
          </AnimateIn>
        </div>
      </GirisKabugu>
    );
  }

  /*
   * §43.2 — BAŞARI EKRANI. "Hesabın oluşturuldu" DEMEZ: var olan bir adresle yapılan
   * kayıt da aynı 200'ü alır (hesap keşfi koruması) ve o durumda yeni bir hesap
   * oluşmamıştır. Panelin bildiği tek doğru, mektubun kuyruğa girdiğidir.
   */
  if (submit.isSuccess) {
    return (
      // Referans check-email ekranı ORTALANMIŞTIR — hero yalnız formun yanında durur.
      <GirisKabugu testId="kayit-sayfasi" duzen="ortala" ustSag={<GirisHapi />}>
        <div data-testid="kayit-eposta-kontrol" className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>E-postanı kontrol et</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60} className="flex flex-col gap-4">
            <KartAciklama>
              <span className="font-medium text-ink">{email.trim().toLowerCase()}</span>{' '}
              adresine bir doğrulama bağlantısı gönderdik. Bağlantı 24 saat geçerlidir;
              tıklamadan giriş yapamazsın.
            </KartAciklama>
            <p className="font-grotesk text-[11px] text-[#686868]">
              Mektup gelmediyse spam klasörüne bakın — bu adres zaten kayıtlıysa yeni bir
              hesap açılmaz.
            </p>
            <Link
              to={LOGIN_PATH}
              className="font-grotesk text-[12px] font-normal text-brand hover:underline"
            >
              Girişe dön
            </Link>
          </AnimateIn>
        </div>
      </GirisKabugu>
    );
  }

  const serverError = registerErrorText(submit.error);
  const error = localProblem ?? serverError;
  const disabled = email.trim() === '' || password === '' || submit.isPending;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (disabled) return;
    const problem = passwordProblem(password);
    setLocalProblem(problem);
    if (problem !== null) return;
    submit.mutate();
  }

  return (
    <GirisKabugu testId="kayit-sayfasi" ustSag={<GirisHapi />}>
      <form className="flex flex-col gap-6" onSubmit={onSubmit} noValidate>
        <AnimateIn delay={0} className="flex flex-col gap-1.5">
          <KartBaslik>Hesap oluştur</KartBaslik>
          <KartAciklama>Adresinizi doğruladıktan sonra ilk projenizi kurabilirsiniz.</KartAciklama>
        </AnimateIn>

        <AnimateIn delay={60} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <AlanEtiketi>Ad soyad</AlanEtiketi>
            <MetinAlani
              data-testid="kayit-ad"
              type="text"
              name="fullName"
              autoComplete="name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Ayşe Yılmaz"
              maxLength={120}
            />
          </div>

          <div className="flex flex-col gap-1">
            <AlanEtiketi>E-posta</AlanEtiketi>
            <MetinAlani
              data-testid="kayit-eposta"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="ad.soyad@sirket.com"
              maxLength={254}
            />
          </div>

          <div className="flex flex-col gap-1">
            <AlanEtiketi>Parola</AlanEtiketi>
            <ParolaAlani
              data-testid="kayit-parola"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                // Kullanıcı yazmaya devam ederken eski uyarı ekranda kalmasın.
                if (localProblem !== null) setLocalProblem(null);
              }}
              placeholder="••••••••••"
            />
            {/* §27.3 politikası önden söylenir — kullanıcı 400 alarak öğrenmemeli. */}
            <span className="font-grotesk text-[11px] text-[#686868]">
              En az {PASSWORD_MIN_LENGTH} karakter, en az 1 harf ve 1 rakam.
            </span>
          </div>

          {error !== null ? <HataSeridi testId="kayit-hata">{error}</HataSeridi> : null}

          <MarkaButonu type="submit" data-testid="kayit-gonder" disabled={disabled}>
            {submit.isPending ? 'Hesap oluşturuluyor…' : 'Hesap oluştur'}
          </MarkaButonu>
        </AnimateIn>
      </form>
    </GirisKabugu>
  );
}
